import { PLAN_TIER_INFO, type PlanTier } from "@gf/shared";
import { discountedCents, type SignupState } from "@gf/shared/signup";
import { Button, Callout, Input, Skeleton } from "@gf/ui";
import { BadgePercent } from "lucide-react";
import { formatUsd } from "../../../lib/billing";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { signupApi } from "./api";
import { errorText, signupFailure } from "./flow";
import { PaymentForm } from "./PaymentForm";
import { PlanPicker } from "./PlanPicker";

/**
 * Step 4: the chosen plan (changeable), Stripe's card form for the card-check hold, the disclosure.
 * `attempt` goes up when the API rejected the last card, so a fresh PaymentIntent is fetched.
 */
export function CardStep({
  session,
  signup,
  attempt,
  cardError,
  onSignupChange,
  onConfirmed,
  onExpired,
}: {
  session: string;
  signup: SignupState;
  attempt: number;
  cardError: string | null;
  onSignupChange: (state: SignupState) => void;
  onConfirmed: () => void;
  onExpired: () => void;
}) {
  const [changing, setChanging] = useState(false);
  const [pick, setPick] = useState<PlanTier>(signup.plan);
  const [planBusy, setPlanBusy] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  // A new PaymentIntent per plan and per rejected card; never reuse an old client secret.
  const [version, setVersion] = useState(0);

  const card = useQuery({
    queryKey: ["signup", "card", session, signup.plan, attempt, version],
    queryFn: () => signupApi.card(session),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const expired = card.isError && signupFailure(card.error) === "expired";
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);

  function openPicker() {
    setPick(signup.plan);
    setPlanError(null);
    setChanging(true);
  }

  async function choosePlan(plan: PlanTier) {
    if (plan === signup.plan) return setChanging(false);
    setPlanBusy(true);
    setPlanError(null);
    try {
      const next = await signupApi.plan(session, plan);
      onSignupChange(next);
      setVersion((v) => v + 1);
      setChanging(false);
    } catch (err) {
      if (signupFailure(err) === "expired") return onExpired();
      setPlanError(errorText(err, "We couldn't change your plan. Try again."));
    } finally {
      setPlanBusy(false);
    }
  }

  const tier = PLAN_TIER_INFO[signup.plan];
  const priceCents = tier.priceMonthly * 100;
  const discounted = discountedCents(priceCents, signup.discount);

  return (
    <div className="gf-stack">
      {changing ? (
        <div className="gf-stack">
          <PlanPicker value={pick} onChange={setPick} legend="Change your plan" disabled={planBusy} discount={signup.discount} />
          {planError ? <Callout tone="danger">{planError}</Callout> : null}
          <div className="signup-actions">
            <Button variant="primary" onClick={() => void choosePlan(pick)} loading={planBusy} disabled={pick === signup.plan}>
              Switch to {PLAN_TIER_INFO[pick].name}
            </Button>
            <Button variant="ghost" onClick={() => setChanging(false)} disabled={planBusy}>
              Keep {tier.name}
            </Button>
          </div>
        </div>
      ) : (
        <div className="signup-chosen">
          <div className="signup-chosen__plan">
            <span className="signup-chosen__eyebrow">Your plan</span>
            <span className="signup-chosen__name">
              {tier.name} <span className="signup-chosen__tagline">· {tier.tagline}</span>
            </span>
          </div>
          <span className="signup-chosen__price">
            {signup.discount ? <s className="signup-chosen__was">{formatUsd(priceCents)}</s> : null}
            {signup.discount ? formatUsd(discounted) : `$${tier.priceMonthly}`}
            <span className="signup-chosen__per">/mo</span>
          </span>
          <Button variant="ghost" size="sm" onClick={openPicker} aria-label={`Change plan (now ${tier.name})`}>
            Change
          </Button>
        </div>
      )}

      <DiscountRow session={session} signup={signup} disabled={changing || planBusy} onSignupChange={onSignupChange} onExpired={onExpired} />

      {card.isPending ? (
        <div className="signup-card" aria-busy="true">
          <div className="signup-card__loading">
            <Skeleton height={14} width="30%" />
            <Skeleton height={44} radius={6} />
            <div className="signup-card__row">
              <Skeleton height={44} radius={6} />
              <Skeleton height={44} radius={6} />
            </div>
          </div>
          <Skeleton lines={3} height={12} />
        </div>
      ) : card.isError ? (
        expired ? null : (
          <Callout
            tone="danger"
            title="We couldn't open the card form"
            action={
              <Button size="sm" onClick={() => void card.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(card.error, "Something went wrong on our end. Try again.")}
          </Callout>
        )
      ) : (
        <PaymentForm
          key={card.data.clientSecret}
          card={card.data}
          name={signup.name}
          email={signup.email}
          discount={signup.discount}
          disabled={changing || planBusy}
          error={cardError}
          onConfirmed={onConfirmed}
        />
      )}
    </div>
  );
}

/** The discount code on this signup (with Remove), or a way to add one before the trial starts. */
function DiscountRow({
  session,
  signup,
  disabled,
  onSignupChange,
  onExpired,
}: {
  session: string;
  signup: SignupState;
  disabled: boolean;
  onSignupChange: (state: SignupState) => void;
  onExpired: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(next: string | null) {
    setBusy(true);
    setError(null);
    try {
      onSignupChange(await signupApi.promo(session, next));
      setOpen(false);
      setCode("");
    } catch (err) {
      if (signupFailure(err) === "expired") return onExpired();
      setError(errorText(err, "We couldn't apply that code. Try again."));
    } finally {
      setBusy(false);
    }
  }

  if (signup.discount) {
    return (
      <div className="signup-discount">
        <BadgePercent size={16} aria-hidden="true" />
        <span className="signup-discount__text">
          <strong>{signup.discount.label}</strong> · code {signup.discount.code}
        </span>
        <Button variant="ghost" size="sm" loading={busy} disabled={disabled} onClick={() => void save(null)} aria-label={`Remove discount code ${signup.discount.code}`}>
          Remove
        </Button>
        {error ? <p className="signup-discount__error">{error}</p> : null}
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" className="signup-code-toggle" disabled={disabled} onClick={() => setOpen(true)}>
        Have a discount code?
      </button>
    );
  }
  return (
    <form
      className="signup-code-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim()) void save(code.trim());
      }}
    >
      <Input
        icon={<BadgePercent size={16} />}
        aria-label="Discount code"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        maxLength={40}
        placeholder="GF25-XXXXXX"
        invalid={!!error}
      />
      <Button type="submit" variant="secondary" loading={busy} disabled={!code.trim() || disabled}>
        Apply
      </Button>
      {error ? <p className="signup-discount__error">{error}</p> : null}
    </form>
  );
}
